var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { discoverSsoConnections, getSsoLoginUrl, } from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.api";
import * as authSelectors from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.selectors";
import { ESsoProvider, } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { getMessageForError } from "@rentiohq/shared-frontend/dist/utils/error.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import { Banner, Button, DisplayText, Link, TextField, TextStyle, } from "../../components";
import { StyledAuthActions, StyledAuthBody, StyledAuthContent, StyledAuthFrame, StyledAuthLogo, StyledAuthMain, StyledAuthMedia, StyledAuthMediaWrap, } from "./Auth.styled";
import { forgetSsoSession, rememberSsoSession, useNavigateAfterSuccess, } from "./Auth.utils";
// The error codes the assertion callback can redirect back with. Anything else — including a
// code added on the API side later — falls back to the generic message rather than rendering
// a raw key.
var KNOWN_ERROR_CODES = [
    "email_in_use",
    "expired",
    "invalid_request",
    "rejected",
    "unavailable",
];
// Survives the full-page trip to the identity provider and back, so a failure lands on this
// page with the address still filled in. sessionStorage rather than a query parameter: the
// API builds the error URL and has no business carrying a personal identifier through it.
var DOMAIN_STORAGE_KEY = "rentio_sso_domain";
// Labels for the providers we know by name. A connection may name any provider — nothing
// branches on the value — so an unknown one falls back to the generic call to action rather
// than rendering a raw enum. Note that i18n-check-keys only sees literal keys, so these two
// are verified by hand.
var PROVIDER_LABEL_KEYS = (_a = {},
    _a[ESsoProvider.MicrosoftEntraId] = "auth.sso.provider.microsoft_entra_id",
    _a);
var getErrorKey = function (error) {
    if (!error) {
        return undefined;
    }
    return KNOWN_ERROR_CODES.includes(error)
        ? "auth.sso.error.".concat(error)
        : "auth.sso.error.generic";
};
var readStoredDomain = function () {
    var _a;
    try {
        return (_a = window.sessionStorage.getItem(DOMAIN_STORAGE_KEY)) !== null && _a !== void 0 ? _a : "";
    }
    catch (_b) {
        // Private mode and blocked site data both throw here. The field simply starts empty.
        return "";
    }
};
var storeDomain = function (domain) {
    try {
        window.sessionStorage.setItem(DOMAIN_STORAGE_KEY, domain);
    }
    catch (_a) {
        // Not being able to remember what was typed is not worth failing the sign-in over.
    }
};
/**
 * The one sign-in page every office shares.
 *
 * A person types their work address, the API says which offices that domain can sign in to,
 * and the browser is handed to that office's identity provider. Deliberately not per office:
 * a URL naming a customer is not a product surface, and nothing here has to be given out
 * ahead of time.
 *
 * `/auth/sso/:slug` still resolves here, skipping straight to the final step. Offices
 * onboarded before this page existed have that link in their own documentation, and breaking
 * it buys nothing.
 */
var SsoSignIn = function () {
    var slug = useParams().slug;
    var queryParams = useQueryParams({
        error: StringParam,
    })[0];
    // Router
    var navigateAfterSuccess = useNavigateAfterSuccess();
    // Redux
    var hasAccessToken = useSelector(authSelectors.hasAccessToken);
    // State
    var _a = React.useState(readStoredDomain), domain = _a[0], setDomain = _a[1];
    var _b = React.useState(), matches = _b[0], setMatches = _b[1];
    var _c = React.useState(), selected = _c[0], setSelected = _c[1];
    var _d = React.useState(false), isSubmitting = _d[0], setIsSubmitting = _d[1];
    var _e = React.useState(), error = _e[0], setError = _e[1];
    // Lifecycle
    React.useEffect(function () {
        if (!hasAccessToken) {
            return;
        }
        navigateAfterSuccess();
    }, [hasAccessToken, navigateAfterSuccess]);
    // Event handlers
    var handleSubmitDomain = function (event) { return __awaiter(void 0, void 0, void 0, function () {
        var result, unknownError_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    event.preventDefault();
                    if (domain.trim() === "") {
                        return [2 /*return*/];
                    }
                    setError(undefined);
                    setIsSubmitting(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, discoverSsoConnections(domain)];
                case 2:
                    result = _a.sent();
                    setMatches(result);
                    // One office is the normal case, so it goes straight to the final step rather than
                    // rendering a list of one.
                    setSelected(result.length === 1 ? result[0] : undefined);
                    if (result.length === 0) {
                        setError(getLocalizedText("auth.sso.error.unknown_domain"));
                    }
                    return [3 /*break*/, 5];
                case 3:
                    unknownError_1 = _a.sent();
                    setError(getMessageForError(unknownError_1));
                    return [3 /*break*/, 5];
                case 4:
                    setIsSubmitting(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var startSignIn = function (slugToUse) {
        // Remembered before leaving, so that when this session ends — refresh is refused for SSO
        // users on purpose, so that happens within the hour — the route guard sends them back
        // here rather than to the password page, which their office does not allow them to use.
        rememberSsoSession();
        // A full navigation, not a fetch: the API answers with a redirect to the identity
        // provider and sets the cookie binding the flow to this browser, and neither survives
        // an XHR.
        window.location.assign(getSsoLoginUrl(slugToUse));
    };
    var handleClickSignIn = function (match) {
        storeDomain(domain);
        startSignIn(match.slug);
    };
    var handleClickChangeAddress = function () {
        setMatches(undefined);
        setSelected(undefined);
        setError(undefined);
    };
    // Render
    // Only while the address form is still showing: once discovery finds a connection the person
    // is moving forward, and the error of the attempt that brought them back would mislead.
    var errorKey = matches ? undefined : getErrorKey(queryParams.error);
    var providerLabelKey = selected
        ? PROVIDER_LABEL_KEYS[selected.provider]
        : undefined;
    var renderFinalStep = function (onContinue, canChangeAddress) { return (_jsxs(_Fragment, { children: [_jsx(StyledAuthActions, { children: _jsx(Button, { appearance: "primary", isFullWidth: true, onClick: onContinue, children: providerLabelKey
                        ? getLocalizedText("auth.sso.continue_with", {
                            provider: getLocalizedText(providerLabelKey),
                        })
                        : getLocalizedText("auth.sso.cta") }) }), canChangeAddress && (_jsx(StyledAuthActions, { children: _jsx(Button, { appearance: "link", onClick: handleClickChangeAddress, children: getLocalizedText("auth.sso.change_address") }) }))] })); };
    var renderBody = function () {
        // The bookmarked per-office link. The slug names a connection the person never chose, so
        // there is nothing to discover and nothing to change — just the one step.
        if (slug) {
            return renderFinalStep(function () { return startSignIn(slug); }, false);
        }
        if (selected) {
            return renderFinalStep(function () { return handleClickSignIn(selected); }, true);
        }
        if (matches && matches.length > 1) {
            return (_jsxs(_Fragment, { children: [_jsx(TextStyle, { variation: "subdued", children: getLocalizedText("auth.sso.office.title") }), matches.map(function (match) {
                        var _a;
                        return (_jsx(StyledAuthActions, { children: _jsx(Button, { appearance: "outline", isFullWidth: true, onClick: function () { return setSelected(match); }, children: (_a = match.brokerName) !== null && _a !== void 0 ? _a : match.slug }) }, match.slug));
                    }), _jsx(StyledAuthActions, { children: _jsx(Button, { appearance: "link", onClick: handleClickChangeAddress, children: getLocalizedText("auth.sso.change_address") }) })] }));
        }
        return (_jsxs("form", { onSubmit: handleSubmitDomain, children: [_jsx(TextField, { id: "sso-domain", name: "domain", 
                    // Deliberately not type="email": the field accepts a bare domain as well as a
                    // full address, and native constraint validation would refuse to submit a bare
                    // one at all. inputMode keeps the e-mail keyboard on mobile and autoComplete
                    // keeps the browser's own suggestions.
                    inputMode: "email", autoComplete: "email", label: getLocalizedText("auth.sso.domain.label"), placeholder: getLocalizedText("auth.sso.domain.placeholder"), value: domain, error: error, onChange: function (e) { return setDomain(e.target.value); } }), _jsx(StyledAuthActions, { children: _jsx(Button, { appearance: "primary", type: "submit", isFullWidth: true, isDisabled: domain.trim() === "" || isSubmitting, isSubmitting: isSubmitting, children: getLocalizedText("auth.sso.domain.submit") }) })] }));
    };
    return (_jsxs(StyledAuthFrame, { children: [_jsx(StyledAuthContent, { children: _jsxs(StyledAuthMain, { children: [_jsx(StyledAuthLogo, {}), _jsxs(StyledAuthBody, { children: [_jsx(DisplayText, { children: getLocalizedText("auth.sso.title") }), errorKey && (_jsx(Banner, { variation: "error", title: getLocalizedText(errorKey) })), _jsx(TextStyle, { variation: "subdued", children: getLocalizedText("auth.sso.info") }), renderBody(), _jsx(StyledAuthActions, { children: _jsx(Link, { to: "/auth/signin", onClick: forgetSsoSession, children: getLocalizedText("auth.sso.use_password_instead") }) })] })] }) }), _jsx(StyledAuthMediaWrap, { children: _jsx(StyledAuthMedia, {}) })] }));
};
// eslint-disable-next-line import/no-default-export
export default SsoSignIn;
